/**
 * Framework-free stale-deployment / missing-chunk recovery shared by the admin,
 * staff (Ops), customer portal and public surfaces.
 *
 * A tab opened before a deployment holds an index.html whose hashed chunks no
 * longer exist, so lazy imports fail (`vite:preloadError`, or a rejected
 * `React.lazy` caught by a route boundary). The recovery rules are:
 *
 *  - a screen that registered dirty or mutation-pending work is NEVER reloaded
 *    on its own; the controller opens a prompt and waits for an explicit choice,
 *  - with no unsaved work the page may reload automatically, but only a bounded
 *    number of times per window, never while offline, and never when the
 *    attempt counter cannot be stored (that would allow a reload loop),
 *  - navigation recovery is separate from mutation outcomes: reload is refused
 *    while a mutation is pending and nothing is ever resubmitted afterwards;
 *    the server state is simply fetched again by the freshly loaded page,
 *  - a reload that is followed by another missing chunk is reported as a
 *    failed recovery attempt instead of looping.
 *
 * Only counters and timestamps are stored here. Draft content goes through the
 * owner-bound draft store, and only when the person chooses to keep it.
 */
export type ChunkRecoveryBlock =
  | 'unsaved' // dirty work registered
  | 'pending' // a mutation is in flight
  | 'offline' // browser reports no connection
  | 'repeated' // automatic attempts exhausted for this window
  | 'storage' // attempt counter cannot be stored, so reloads cannot be bounded
  | 'failed' // a previous reload did not fix the missing chunk
  | 'clean'; // nothing to protect, update is ready (user decides)

export interface UnsavedWorkItem {
  id: string;
  label: string;
  dirty: boolean;
  pending: boolean;
  /** True when the screen can store its draft for the reload (or already did). */
  canKeepDraft: boolean;
  /** Extra explicit loss, e.g. selected files that cannot be stored. */
  lossNote?: string;
  /**
   * The screen itself is already gone (a route boundary replaced it after a missing chunk) and only
   * its unsaved fields are kept so the person can still choose to keep or discard them.
   */
  retained?: boolean;
}

export interface ChunkRecoverySnapshot {
  open: boolean;
  block: ChunkRecoveryBlock | null;
  items: readonly UnsavedWorkItem[];
  hasDirty: boolean;
  hasPending: boolean;
  /** True when a requested reload could not keep every dirty draft. */
  draftNotKept: boolean;
  /** True after the browser rejected the reload call itself. */
  reloadFailed: boolean;
}

export interface WorkRegistration {
  label: string;
  dirty: boolean;
  pending: boolean;
  /** Stores the draft; returns false when it could not be stored. Omit when drafts are not supported. */
  persist?: () => boolean;
  /** Drops anything that was stored for this work. */
  discard?: () => void;
  lossNote?: string;
}

export interface WorkHandle {
  update(patch: Partial<WorkRegistration>): void;
  release(): void;
}

export interface RecoveryStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * A source of "a mutation is in flight" that outlives any single screen, typically the app's
 * QueryClient mutation cache. The controller consults it for work retained after a route boundary
 * replaced its screen, because the screen's own pending flag can no longer be updated.
 */
export interface PendingSource {
  isPending(): boolean;
  subscribe(listener: () => void): () => void;
}

export interface PendingSourceRegistry extends PendingSource {
  /** Adds one more source (e.g. one QueryClient). Returns the function that removes it again. */
  attach(source: PendingSource): () => void;
}

/** Combines the pending state of every attached source; usable before the sources exist. */
export function createPendingSourceRegistry(): PendingSourceRegistry {
  const sources = new Map<PendingSource, () => void>();
  const listeners = new Set<() => void>();
  const notify = () => {
    listeners.forEach((listener) => {
      try {
        listener();
      } catch {
        // one failing listener must not stop the others
      }
    });
  };
  return {
    isPending() {
      for (const source of sources.keys()) {
        try {
          if (source.isPending()) return true;
        } catch {
          // an unreadable source cannot prove that a mutation is running
        }
      }
      return false;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    attach(source) {
      if (!sources.has(source)) {
        let unsubscribe = () => {};
        try {
          unsubscribe = source.subscribe(notify);
        } catch {
          // a source that cannot notify is still polled by isPending()
        }
        sources.set(source, unsubscribe);
        notify();
      }
      return () => {
        const stop = sources.get(source);
        if (!stop) return;
        sources.delete(source);
        stop();
        notify();
      };
    },
  };
}

export interface ChunkRecoveryOptions {
  /** Storage key namespace, e.g. "mbbe-admin". */
  namespace: string;
  /**
   * Mutation state that survives a screen. Without it, a mutation that was in flight when a route
   * boundary replaced its screen is no longer tracked (the screen's pending flag is gone).
   */
  pendingSource?: PendingSource;
  storage?: () => RecoveryStorage | null;
  now?: () => number;
  reload?: () => void;
  isOnline?: () => boolean;
  /** Maximum automatic reloads inside `windowMs`. */
  maxAutoReloads?: number;
  windowMs?: number;
  /** A missing chunk this soon after a reload means the reload did not help. */
  failedWithinMs?: number;
}

export type ReloadRequest = { keepDrafts: boolean };
export type ReloadOutcome = 'reloading' | 'blocked-pending' | 'draft-not-kept' | 'failed';

export interface ChunkRecoveryController {
  getSnapshot(): ChunkRecoverySnapshot;
  subscribe(listener: () => void): () => void;
  register(registration: WorkRegistration): WorkHandle;
  /** A chunk or preload failure was observed. Never throws. */
  report(): void;
  /**
   * A route boundary is about to replace the registered screens because their next route could not
   * be loaded. Dirty work is moved to a retained list so it survives the unmount: the prompt keeps
   * offering to keep or discard it, and "keep" still stores the last field values. Work that was
   * mutation-pending keeps blocking the reload for as long as the configured `pendingSource` reports
   * a mutation in flight (the screen's own flag can no longer settle). Retained work is cleared once
   * a reload is requested and not refused.
   */
  retainWork(): void;
  /** Explicit user choice. Drafts are kept only when `keepDrafts` is set and every dirty item could persist. */
  reload(request: ReloadRequest): ReloadOutcome;
  dismiss(): void;
}

const IDLE: ChunkRecoverySnapshot = {
  open: false,
  block: null,
  items: [],
  hasDirty: false,
  hasPending: false,
  draftNotKept: false,
  reloadFailed: false,
};

const CHUNK_ERROR_MESSAGES = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'error loading dynamically imported module',
  'Unable to preload CSS',
];

/** True for the failures a missing/stale lazy chunk produces. */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === 'ChunkLoadError') return true;
  return CHUNK_ERROR_MESSAGES.some((message) => error.message.includes(message));
}

function defaultStorage(): RecoveryStorage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

interface AttemptRecord {
  count: number;
  startedAt: number;
}

export function createChunkRecovery(options: ChunkRecoveryOptions): ChunkRecoveryController {
  const now = options.now ?? Date.now;
  const maxAutoReloads = options.maxAutoReloads ?? 2;
  const windowMs = options.windowMs ?? 5 * 60 * 1000;
  const failedWithinMs = options.failedWithinMs ?? 60 * 1000;
  const autoKey = `${options.namespace}:chunk-auto-reload`;
  const manualKey = `${options.namespace}:chunk-manual-reload`;
  const isOnline = options.isOnline ?? (() => typeof navigator === 'undefined' || navigator.onLine !== false);
  const reloadPage =
    options.reload ??
    (() => {
      window.location.reload();
    });
  const storage = (): RecoveryStorage | null => {
    try {
      return (options.storage ?? defaultStorage)();
    } catch {
      return null;
    }
  };

  interface RetainedWork {
    work: WorkRegistration;
    /** The screen was waiting for a mutation when it went away; the pending source now speaks for it. */
    trackPending: boolean;
  }
  const works = new Map<symbol, WorkRegistration>();
  const retained = new Map<symbol, RetainedWork>();
  let pendingUnsubscribe: (() => void) | null = null;
  const externalPending = (): boolean => {
    try {
      return Boolean(options.pendingSource?.isPending());
    } catch {
      return false;
    }
  };
  const retainedPending = (entry: RetainedWork): boolean => entry.trackPending && externalPending();
  const stopWatchingPending = (): void => {
    pendingUnsubscribe?.();
    pendingUnsubscribe = null;
  };
  const listeners = new Set<() => void>();
  let state: { open: boolean; block: ChunkRecoveryBlock | null; draftNotKept: boolean; reloadFailed: boolean } = {
    open: false,
    block: null,
    draftNotKept: false,
    reloadFailed: false,
  };
  let snapshot: ChunkRecoverySnapshot = IDLE;

  const buildSnapshot = (): ChunkRecoverySnapshot => {
    if (!state.open) return IDLE;
    const items: UnsavedWorkItem[] = [];
    const add = (work: WorkRegistration, isRetained: boolean) => {
      if (!work.dirty && !work.pending) return;
      items.push({
        id: work.label,
        label: work.label,
        dirty: work.dirty,
        pending: work.pending,
        canKeepDraft: Boolean(work.persist),
        lossNote: work.lossNote,
        ...(isRetained ? { retained: true } : {}),
      });
    };
    works.forEach((work) => add(work, false));
    retained.forEach((entry) => add({ ...entry.work, pending: retainedPending(entry) }, true));
    const hasDirty = items.some((item) => item.dirty);
    const hasPending = items.some((item) => item.pending);
    // Once the protected work is gone the prompt degrades to a plain "update ready" choice.
    const block: ChunkRecoveryBlock | null =
      state.block === 'unsaved' || state.block === 'pending'
        ? hasPending
          ? 'pending'
          : hasDirty
            ? 'unsaved'
            : 'clean'
        : state.block;
    return {
      open: true,
      block,
      items,
      hasDirty,
      hasPending,
      draftNotKept: state.draftNotKept,
      reloadFailed: state.reloadFailed,
    };
  };

  const same = (a: ChunkRecoverySnapshot, b: ChunkRecoverySnapshot): boolean =>
    a.open === b.open &&
    a.block === b.block &&
    a.hasDirty === b.hasDirty &&
    a.hasPending === b.hasPending &&
    a.draftNotKept === b.draftNotKept &&
    a.reloadFailed === b.reloadFailed &&
    a.items.length === b.items.length &&
    a.items.every((item, index) => {
      const other = b.items[index];
      return (
        item.label === other.label &&
        item.dirty === other.dirty &&
        item.pending === other.pending &&
        item.canKeepDraft === other.canKeepDraft &&
        item.lossNote === other.lossNote &&
        item.retained === other.retained
      );
    });

  const publish = (): void => {
    const next = buildSnapshot();
    if (same(snapshot, next)) return;
    snapshot = next;
    listeners.forEach((listener) => listener());
  };

  const open = (block: ChunkRecoveryBlock): void => {
    // A further missing chunk while the prompt already shows the same situation must not wipe what the
    // person has just learned (drafts that could not be stored, a reload the browser rejected).
    if (state.open && state.block === block) {
      publish();
      return;
    }
    state = { open: true, block, draftNotKept: false, reloadFailed: false };
    publish();
  };

  const readAttempt = (store: RecoveryStorage): AttemptRecord => {
    const raw = store.getItem(autoKey);
    if (!raw) return { count: 0, startedAt: 0 };
    try {
      const parsed = JSON.parse(raw) as Partial<AttemptRecord>;
      if (typeof parsed.count === 'number' && typeof parsed.startedAt === 'number') {
        return { count: parsed.count, startedAt: parsed.startedAt };
      }
    } catch {
      // corrupt counter: start a fresh window
    }
    return { count: 0, startedAt: 0 };
  };

  const recentManualReload = (): boolean => {
    try {
      const raw = storage()?.getItem(manualKey);
      const at = raw ? Number(raw) : 0;
      return at > 0 && now() - at < failedWithinMs;
    } catch {
      return false;
    }
  };

  const hasProtectedWork = (): { dirty: boolean; pending: boolean } => {
    let dirty = false;
    let pending = false;
    works.forEach((work) => {
      dirty = dirty || work.dirty;
      pending = pending || work.pending;
    });
    retained.forEach((entry) => {
      dirty = dirty || entry.work.dirty;
      pending = pending || retainedPending(entry);
    });
    return { dirty, pending };
  };

  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    register(registration) {
      const id = Symbol('work');
      works.set(id, { ...registration });
      publish();
      return {
        update(patch) {
          const current = works.get(id);
          if (!current) return;
          works.set(id, { ...current, ...patch });
          publish();
        },
        release() {
          if (works.delete(id)) publish();
        },
      };
    },
    retainWork() {
      try {
        let movedDirty = false;
        let movedPending = false;
        works.forEach((work, id) => {
          works.delete(id);
          const trackPending = work.pending && Boolean(options.pendingSource);
          if (!work.dirty && !trackPending) return;
          // The flag of the unmounting screen can never settle, so the pending source decides from here on.
          retained.set(id, { work: { ...work, pending: false }, trackPending });
          movedDirty = movedDirty || work.dirty;
          movedPending = movedPending || trackPending;
        });
        if (retained.size > 0 && options.pendingSource && !pendingUnsubscribe) {
          pendingUnsubscribe = options.pendingSource.subscribe(() => {
            // Entries that only waited for a mutation are done once it settles; dirty ones stay until chosen.
            retained.forEach((entry, id) => {
              if (!entry.work.dirty && !retainedPending(entry)) retained.delete(id);
            });
            if (retained.size === 0) stopWatchingPending();
            publish();
          });
        }
        // Same choice as for a mounted screen, even when no preload event announced the failure.
        if (movedPending) open('pending');
        else if (movedDirty) open('unsaved');
        else publish();
      } catch {
        // retaining is best effort; recovery must never crash the page that is already recovering
      }
    },
    report() {
      try {
        const { dirty, pending } = hasProtectedWork();
        if (pending) return open('pending');
        if (dirty) return open('unsaved');
        if (recentManualReload()) return open('failed');
        if (!isOnline()) return open('offline');
        const store = storage();
        if (!store) return open('storage');
        let attempt: AttemptRecord;
        try {
          attempt = readAttempt(store);
        } catch {
          return open('storage');
        }
        const at = now();
        const inWindow = attempt.count > 0 && at - attempt.startedAt < windowMs;
        const next: AttemptRecord = inWindow ? { count: attempt.count + 1, startedAt: attempt.startedAt } : { count: 1, startedAt: at };
        if (inWindow && attempt.count >= maxAutoReloads) return open('repeated');
        try {
          // The attempt is recorded BEFORE reloading so a throwing store can never allow a loop.
          store.setItem(autoKey, JSON.stringify(next));
        } catch {
          return open('storage');
        }
        try {
          reloadPage();
        } catch {
          state = { open: true, block: 'failed', draftNotKept: false, reloadFailed: true };
          publish();
        }
      } catch {
        // recovery must never crash the page that is already recovering
        open('failed');
      }
    },
    reload(request) {
      const { pending } = hasProtectedWork();
      if (pending) {
        // A pending mutation has an unknown outcome; leaving now would hide it.
        if (!state.open) open('pending');
        return 'blocked-pending';
      }
      // Retained work is older than anything that registered afterwards, so it is stored first and a
      // live registration with the same draft key writes last and wins.
      const dirtyWorks: WorkRegistration[] = [];
      retained.forEach((entry) => {
        if (entry.work.dirty) dirtyWorks.push(entry.work);
      });
      works.forEach((work) => {
        if (work.dirty) dirtyWorks.push(work);
      });
      if (request.keepDrafts) {
        let allKept = true;
        for (const work of dirtyWorks) {
          let kept = false;
          try {
            kept = Boolean(work.persist?.());
          } catch {
            kept = false;
          }
          if (!kept) allKept = false;
        }
        if (!allKept) {
          state = { open: true, block: state.block ?? 'unsaved', draftNotKept: true, reloadFailed: false };
          publish();
          return 'draft-not-kept';
        }
      } else {
        dirtyWorks.forEach((work) => {
          try {
            work.discard?.();
          } catch {
            // discarding is best effort
          }
        });
        // Discarded work must not come back in a later prompt, even if the reload call is refused.
        retained.clear();
        stopWatchingPending();
      }
      try {
        storage()?.setItem(manualKey, String(now()));
      } catch {
        // the failed-attempt marker is a diagnostic only
      }
      try {
        reloadPage();
      } catch {
        state = { open: true, block: 'failed', draftNotKept: false, reloadFailed: true };
        publish();
        return 'failed';
      }
      // The reload is under way and the drafts are stored: nothing is left to retain for this page.
      retained.clear();
      stopWatchingPending();
      return 'reloading';
    },
    dismiss() {
      state = { open: false, block: null, draftNotKept: false, reloadFailed: false };
      publish();
    },
  };
}

/** Listens to Vite's preload failure event. The event is left alone so route boundaries still see the error. */
export function installPreloadErrorGuard(
  controller: ChunkRecoveryController,
  target: Pick<EventTarget, 'addEventListener' | 'removeEventListener'> = window,
): () => void {
  const listener = () => controller.report();
  target.addEventListener('vite:preloadError', listener);
  return () => target.removeEventListener('vite:preloadError', listener);
}
